import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { FeatureFlagProvider } from "@/shared/featureFlags/provider";
import { useApplyTheme } from "@/shared/hooks/useApplyTheme";
import { DialogProvider, OfflineBanner, ToastProvider } from "@/shared/ui";

// Port de src/app/providers/AppProviders.tsx.
//
// Les providers natifs disparaissent : `SafeAreaProvider` (les safe-areas
// passent par `env(safe-area-inset-*)` en CSS) et `GestureHandlerRootView`
// (les gestes passent par les événements pointeur du DOM).

export function AppProviders({ children }: { children: ReactNode }) {
  // Applique le thème et suit ses changements — monté ICI, à la racine unique
  // de l'app, plutôt que dans chacun des deux shells : un abonnement suffit, et
  // les écrans hors shell (connexion, erreur) en profitent aussi.
  useApplyTheme();

  // Instancié dans un état pour survivre au Fast Refresh sans recréer le cache.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            staleTime: 30_000,
            // Sur natif, le refetch au retour d'app passait par AppState ;
            // react-query le gère nativement sur web via l'événement focus.
            refetchOnWindowFocus: true,
          },
        },
      }),
    );

  return (
    <QueryClientProvider client={queryClient}>
      <FeatureFlagProvider>
        <ToastProvider>
          <DialogProvider>
            {/* Le bandeau hors-ligne POUSSE le contenu au lieu de le recouvrir :
                il occupe sa propre bande en haut de l'app, quel que soit
                l'écran affiché. Monté ici, une seule fois — jamais par écran. */}
            <div className="flex h-dvh flex-col">
              <OfflineBanner />
              <div className="min-h-0 flex-1">{children}</div>
            </div>
          </DialogProvider>
        </ToastProvider>
      </FeatureFlagProvider>
    </QueryClientProvider>
  );
}
